/**
 * DocAnnotationToolbarComponent
 * Document-module copy of the phases AnnotationToolbar — the dark editor toolbar:
 * drawing tools, color palette, stroke width, opacity, undo/redo/delete, show/hide
 * toggles, zoom, and export. Stateless — emits events to the document-image-annotator host.
 */
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { DrawingTool, ANNOTATION_COLORS, STROKE_WIDTHS } from './media-annotation.types';

interface ToolDef { id: DrawingTool; icon: string; label: string; key: string; }

@Component({
  standalone: false,
  selector: 'app-doc-annotation-toolbar',
  templateUrl: './doc-annotation-toolbar.component.html',
  styleUrls: ['./doc-annotation-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocAnnotationToolbarComponent {
  @Input() activeTool: DrawingTool = 'select';
  @Input() selectedColor = '#FF6B35';
  @Input() strokeWidth = 4;
  @Input() opacity = 1;
  @Input() canUndo = false;
  @Input() canRedo = false;
  @Input() hasSelection = false;
  @Input() showAnnotations = true;
  @Input() showComments = true;
  @Input() zoomLevel = 1;
  @Input() isFullscreen = false;

  @Output() toolChange = new EventEmitter<DrawingTool>();
  @Output() colorChange = new EventEmitter<string>();
  @Output() strokeChange = new EventEmitter<number>();
  @Output() opacityChange = new EventEmitter<number>();
  @Output() undo = new EventEmitter<void>();
  @Output() redo = new EventEmitter<void>();
  @Output() deleteSelected = new EventEmitter<void>();
  @Output() toggleAnnotations = new EventEmitter<void>();
  @Output() toggleComments = new EventEmitter<void>();
  @Output() zoomIn = new EventEmitter<void>();
  @Output() zoomOut = new EventEmitter<void>();
  @Output() export = new EventEmitter<void>();
  @Output() toggleFullscreen = new EventEmitter<void>();

  readonly tools: ToolDef[] = [
    { id: 'select', icon: 'open_with', label: 'Select / Pan', key: 'V' },
    { id: 'pen', icon: 'edit', label: 'Pen', key: 'P' },
    { id: 'arrow', icon: 'arrow_right_alt', label: 'Arrow', key: 'A' },
    { id: 'rectangle', icon: 'crop_square', label: 'Rectangle', key: 'R' },
    { id: 'circle', icon: 'circle', label: 'Circle', key: 'C' },
    { id: 'text', icon: 'title', label: 'Text', key: 'T' },
    { id: 'ruler', icon: 'straighten', label: 'Measure Distance', key: 'M' },
    { id: 'area', icon: 'square_foot', label: 'Measure Area', key: '' }
  ];

  readonly colors = ANNOTATION_COLORS;
  readonly strokeWidths = STROKE_WIDTHS;

  get zoomPercent(): number {
    return Math.round(this.zoomLevel * 100);
  }

  dotSize(w: number): number {
    return Math.min(w, 12);
  }
}
